import authenticationRoutes from '../../authentication/components/Routes';
import GeneralContentPage from '../../generalContent/components/GeneralContentPage';
import React, { LazyExoticComponent } from 'react';
import taskListRoutes from '../../taskList/components/Routes';
import withTaskFlow from '../../taskList/components/WithTaskFlow';
import { Route, RouteComponentProps, Switch, useLocation } from 'react-router-dom';
import { routeMappings } from '../../constants';
import { SplashPage, ResponseReuseMessagePage } from '../../common/components';
import TcPlusSplashPage from './TcPlusSplashPage';
import { withBasicDetails } from './WithBasicDetails';
import { AnimatePresence } from 'framer-motion';
import configurablePageRoutes from '../../workflowConfigurablePages/components/Routes';
import { ReAttemptPage } from '../../common/components/ReAttemptPage';
import basicTaskListRoutes from '../../basicTaskList/components/Routes';
import { TemplateLayout } from '../../store/interfaces';
import { useAppSelector } from '../../redux-store/hook';

const BiodataPage = React.lazy(() => import('../../biodata/components/BiodataPage'));
const DPNPage = React.lazy(() => import('../../dpn/components/DpnPage'));
const ErrorPage = React.lazy(() => import('../../common/components/ErrorPage'));
const ExpiredLinkPage = React.lazy(() => import('../../common/components/ExpiredLinkPage'));
const FeedbackPage = React.lazy(() => import('../../candidateFeedback/components/CandidateFeedbackPage'));
const MfsRoutes = React.lazy(() => import('../../survey/components/MfsRoutes'));
const SurveyTaskListPage = React.lazy(() => import('../../survey/components/surveyTaskHomePage/SurveyTaskListPage'));
const CandidateConsentPage = React.lazy(() => import('../../proctoring/components/CandidateConsentPage'));
const ProctoringCameraSetupPage = React.lazy(() => import('../../proctoring/components/ProctoringCameraSetupPage'));
const LinkInvalidPage = React.lazy(() => import('./../../common/components/LinkInvalidPage'));
const LockDownBrowserPage = React.lazy(() => import('../../staticPages/components/LockDownBrowserPage'));

export interface CustomProps {
  pageName?: string;
}
export interface RoutePageProps extends RouteComponentProps {
  customProps?: CustomProps;
  routes?: RouteParams[];
}

export interface RouteParams {
  path: string;
  exact?: boolean;
  strict?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: LazyExoticComponent<any> | ((props: RoutePageProps) => JSX.Element);
  routes?: RouteParams[];
  customProps?: CustomProps;
}

const routes: RouteParams[] = [
  {
    path: routeMappings['INIT_TCPLUS_EXPERIENCE'],
    exact: true,
    component: TcPlusSplashPage,
  },
  {
    path: routeMappings['INIT_EXPERIENCE'],
    exact: true,
    component: withBasicDetails(SplashPage),
  },
  {
    path: routeMappings['SECURE_BROWSER'],
    exact: true,
    component: LockDownBrowserPage,
  },
  {
    path: routeMappings['DPN'],
    exact: true,
    component: withBasicDetails(DPNPage),
  },
  {
    path: routeMappings['CANDIDATE_CONSENT'],
    exact: true,
    component: withBasicDetails(CandidateConsentPage),
  },
  {
    path: `${routeMappings['GENCONTENT']}/:id`,
    exact: true,
    component: withBasicDetails(GeneralContentPage),
  },
  {
    path: `${routeMappings['MFSTASKLIST']}/:id`,
    exact: true,
    component: withBasicDetails(SurveyTaskListPage),
  },
  {
    path: routeMappings['EXPIRED_LINK'],
    exact: true,
    component: ExpiredLinkPage,
  },
  {
    path: routeMappings['LINK_INVALID'],
    exact: true,
    component: LinkInvalidPage,
  },
  {
    path: routeMappings['RE_ATTEMPT'],
    exact: true,
    component: withBasicDetails(ReAttemptPage),
  },
  {
    path: routeMappings['VIDEO_FEEDBACK'],
    exact: true,
    component: withBasicDetails(withTaskFlow(FeedbackPage)),
  },
  {
    path: routeMappings['FEEDBACK'],
    exact: true,
    component: withBasicDetails(FeedbackPage),
  },
  {
    path: `${routeMappings['MFS']}/`,
    exact: false,
    strict: true,
    component: MfsRoutes,
  },
  {
    path: routeMappings['PROCTORING_SETUP'],
    exact: true,
    component: withBasicDetails(ProctoringCameraSetupPage),
  },
  {
    path: `${routeMappings['BIODATA']}/:id`,
    exact: true,
    component: withBasicDetails(BiodataPage),
  },
  {
    path: `${routeMappings['INFO']}/:responseReuseType`,
    exact: true,
    component: withBasicDetails(ResponseReuseMessagePage),
  },
  {
    path: '*',
    exact: true,
    component: ErrorPage,
  },
];

export const RouteWithSubRoutes = (route: RouteParams): JSX.Element => {
  return (
    <Route
      path={route.path}
      exact={route.exact}
      render={(props: RoutePageProps): JSX.Element => (
        <route.component {...props} customProps={route.customProps} routes={route.routes} />
      )}
    />
  );
};

const Wrapper = ({ animate, children }: { animate: boolean; children: JSX.Element }): JSX.Element => {
  if (animate) {
    return (
      <AnimatePresence exitBeforeEnter initial={false}>
        {children}
      </AnimatePresence>
    );
  }
  return children;
};

const Routes = (): JSX.Element => {
  const { layoutCode } = useAppSelector((state) => state.template);
  const location = useLocation();

  return (
    <Wrapper animate={layoutCode === TemplateLayout.TEMPLATE_LAYOUT_02}>
      <Switch location={location} key={location.pathname}>
        {authenticationRoutes.map((route: RouteParams, i: number) => (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props: RoutePageProps): JSX.Element => (
              <route.component {...props} customProps={route.customProps} routes={route.routes} />
            )}
          />
        ))}
        {basicTaskListRoutes.map((route: RouteParams, i: number) => (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props: RoutePageProps): JSX.Element => (
              <route.component {...props} customProps={route.customProps} routes={route.routes} />
            )}
          />
        ))}
        {taskListRoutes.map((route: RouteParams, i: number) => (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props: RoutePageProps): JSX.Element => (
              <route.component {...props} customProps={route.customProps} routes={route.routes} />
            )}
          />
        ))}
        {configurablePageRoutes.map((route: RouteParams, i: number) => (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props: RoutePageProps): JSX.Element => (
              <route.component {...props} customProps={route.customProps} routes={route.routes} />
            )}
          />
        ))}
        {routes.map((route: RouteParams, i: number) => (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props: RoutePageProps): JSX.Element => (
              <route.component {...props} customProps={route.customProps} routes={route.routes} />
            )}
          />
        ))}
      </Switch>
    </Wrapper>
  );
};

export default Routes;
